import { Controller } from "@hotwired/stimulus";

// データ出力機能(新規出力)画面の出力ボタン用。
// フォームを fetch で POST して出力履歴を作成し、完了するまで show をポーリングしてダウンロードリンクに切り替える。
export default class DataExportButtonController extends Controller<HTMLElement> {
  static targets = ["initial", "loading", "completed", "downloadLink", "error"];
  static values = { pollUrl: String, failedMessage: String };

  declare readonly initialTarget: HTMLElement;
  declare readonly loadingTarget: HTMLElement;
  declare readonly completedTargets: HTMLElement[];
  declare readonly downloadLinkTarget: HTMLAnchorElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly pollUrlValue: string;
  declare readonly failedMessageValue: string;

  private static readonly POLLING_INTERVAL_MS = 3000;
  private static readonly MAX_CONSECUTIVE_POLL_FAILURES = 3;

  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private consecutivePollFailures = 0;

  disconnect(): void {
    this.stopPolling();
  }

  download(): void {
    this.showState("initial");
  }

  async submit(): Promise<void> {
    const form = this.element.closest("form");
    if (!form) return;

    this.showState("loading");

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        credentials: "same-origin",
      });
      const data = (await response.json()) as { id?: string; errors?: string[] };

      if (!response.ok || !data.id) {
        this.showError(data.errors?.join("\n") || this.failedMessageValue);
        return;
      }
      this.startPolling(data.id);
    } catch {
      this.showError(this.failedMessageValue);
    }
  }

  private showState(state: "initial" | "loading" | "completed"): void {
    this.errorTarget.classList.add("hidden");
    this.initialTarget.classList.toggle("hidden", state !== "initial");
    this.loadingTarget.classList.toggle("hidden", state !== "loading");
    this.completedTargets.forEach((el) => el.classList.toggle("hidden", state !== "completed"));
  }

  private showError(message: string): void {
    this.showState("initial");
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  private startPolling(id: string): void {
    const url = this.pollUrlValue.replace(":id", id);
    this.consecutivePollFailures = 0;

    const poll = async (): Promise<void> => {
      try {
        const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = (await response.json()) as { status: string; download_url: string | null };
        this.consecutivePollFailures = 0;

        if (data.status === "completed" && data.download_url) {
          this.downloadLinkTarget.href = data.download_url;
          this.showState("completed");
        } else if (data.status === "failed") {
          this.showError(this.failedMessageValue);
        } else {
          this.pollingTimer = setTimeout(poll, DataExportButtonController.POLLING_INTERVAL_MS);
        }
      } catch {
        // 一時的な通信失敗をジョブ失敗と誤認しないよう、連続失敗が既定回数に達するまではポーリングを継続する
        this.consecutivePollFailures += 1;
        if (this.consecutivePollFailures >= DataExportButtonController.MAX_CONSECUTIVE_POLL_FAILURES) {
          this.showError(this.failedMessageValue);
          return;
        }
        this.pollingTimer = setTimeout(poll, DataExportButtonController.POLLING_INTERVAL_MS);
      }
    };

    this.pollingTimer = setTimeout(poll, DataExportButtonController.POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  private csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
  }
}
